import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart } from '../../../../types/Chart/Chart';
import useChart from '../../../../hooks/useChart';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { NavForward } from '../../../../components/buttons/NavForward';
import { NextSection } from '../../../../components/buttons/NextSection';
import { OptionVideos } from '../../../../types/Education/OptionVideosEnum';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { Heading } from '../../../../components/Heading';
import { TreatmentOptions, TreatmentOptionsType } from '../../../../types/Treatment/treatmentOptions';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { Button } from '../../../../components';
import { FieldInfo } from '../../../../components/FieldInfo';
import { FormSelect } from '../../../../components/forms/FormSelect';
import { getUnattestedScript } from '../../../../data/treatment/getUnattestedScript';
import { ScriptInput } from '../../../../components/forms/ScriptInput';
import { handleAutomaticTreatmentOptionUpdateProps } from '../../../../types/Treatment/handleAutomaticTreatmentOptionUpdateProps';
import * as Sentry from '@sentry/browser';

export const Route = createFileRoute('/_authenticated/$patientId/education/treatmentOption')({
  component: TreatmentOptionPage,
});

function TreatmentOptionPage() {
  const { mutate } = useUpdateChartSection();
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const treatmentCodes = Object.keys(TreatmentOptions);
  const treatmentOptionsArray = Object.keys(TreatmentOptions).map((label, i) => ({
    name: i + 1 + ' - ' + TreatmentOptions[label as TreatmentOptionsType],
    value: label as TreatmentOptionsType,
  }));

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({ currentPageName: 'treatmentOption', patientInfo, value: true });
    }
  }, [patientInfo]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart && patientInfo.navObj) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'treatmentOption',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chart, patientInfo]);

  // INITIALIZE FORM
  const treatmentOptionForm = useForm({
    defaultValues: {
      code: chart?.SELECTED_TREATMENT_OPTION?.code ? chart.SELECTED_TREATMENT_OPTION.code : 'fullCode',
      description: chart?.SELECTED_TREATMENT_OPTION?.description
        ? chart.SELECTED_TREATMENT_OPTION.description
        : TreatmentOptions.fullCode,
      unAttestedScript: chart?.SELECTED_TREATMENT_OPTION?.unAttestedScript
        ? chart.SELECTED_TREATMENT_OPTION.unAttestedScript
        : '',
    },
    validators: {
      onChange({ value }) {
        if (!treatmentCodes.includes(value.code)) {
          return { code: 'Please select a code' };
        }
        if (!value.description) {
          return { description: 'Please select a code' };
        }
        if (!value.unAttestedScript) {
          return { unAttestedScript: 'Script cannot be empty' };
        }
      },
    },
    onSubmit: () => {
      handleTreatmentOptionUpdate();
    },
  });

  const handleTreatmentOptionUpdate = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          SELECTED_TREATMENT_OPTION: {
            code: treatmentOptionForm.state.values.code,
            description: treatmentOptionForm.state.values.description,
            unAttestedScript: treatmentOptionForm.state.values.unAttestedScript,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );

    mutate(
      {
        patientId: patientId,
        docData: {
          SCRIPT_REVIEW: {
            attestedCode: treatmentOptionForm.state.values.code,
            scriptAttested: false,
            speaker: chart.SCRIPT_REVIEW?.speaker ? chart.SCRIPT_REVIEW.speaker : 'patient',
            telePrompterScript: treatmentOptionForm.state.values.unAttestedScript,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  const handleAutomaticTreatmentOptionUpdate = (payload: handleAutomaticTreatmentOptionUpdateProps) => {
    mutate(
      {
        patientId: patientId,
        docData: {
          SELECTED_TREATMENT_OPTION: {
            code: payload.code,
            description: payload.description,
            unAttestedScript: payload.unAttestedScript,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
    if (chart?.SCRIPT_REVIEW?.attestedCode) {
      mutate(
        {
          patientId: patientId,
          docData: {
            SCRIPT_REVIEW: {
              attestedCode: '',
              scriptAttested: false,
              telePrompterScript: '',
              speaker: chart.SCRIPT_REVIEW?.speaker ? chart.SCRIPT_REVIEW.speaker : 'patient',
            },
          },
        },
        {
          onSuccess: () => {
            // alert('Successfully updated document');
          },
          onError: (error) => {
            Sentry.captureException(error);
            alert(`Error updating document: ${error.message}`); // TELL THE USER
            console.error(error); // GET THE STACKTRACE
          },
        },
      );
    }
  };

  const updateScript = (code: TreatmentOptionsType) => {
    // GENERATE SCRIPT
    const script = getUnattestedScript({ code, chart, patientInfo });

    // UPDATE FIRESTORE WITH SCRIPT - THIS WILL REFRESH THE SCRIPT COMPONENT
    const payload = {
      code: code,
      description: TreatmentOptions[code],
      unAttestedScript: script,
    };
    handleAutomaticTreatmentOptionUpdate(payload);
  };

  // INITIALIZE DOCUMENT IF NONE EXISTS
  React.useEffect(() => {
    if (chart && !chart.SELECTED_TREATMENT_OPTION) {
      const script = getUnattestedScript({ code: 'fullCode', chart, patientInfo });
      handleAutomaticTreatmentOptionUpdate({
        code: 'fullCode',
        description: TreatmentOptions.fullCode,
        unAttestedScript: script,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart, patientInfo]);

  // SET UNATTESTED SCRIPT IF IT EXISTS IN THE CHART
  React.useEffect(() => {
    if (chart?.SELECTED_TREATMENT_OPTION?.unAttestedScript) {
      treatmentOptionForm.setFieldValue('unAttestedScript', chart.SELECTED_TREATMENT_OPTION.unAttestedScript);
    }
  }, [chart, treatmentOptionForm]);

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }

  // SELF-GUIDED PATIENTS ONLY EVER SEE FULL CODE VIDEO
  if (currentUser?.uid === patientId && patientInfo.navType?.base === 'self') {
    return (
      <>
        <div id="treatment-option-page" className="flex flex-col items-center justify-center">
          <Heading id="treatment-option-page-header" textContent="Code-Specific Educational Video" />
          <p className="text-xl">
            Below is a video that summarizes the the treatment options you are electing to receive.
          </p>
          {/* <div id="treatment-option-page-main" className="mt-4 flex w-5/6 flex-row justify-around"> */}
          <div id="treatment-option-video-container" className="mt-4 flex flex-col items-center">
            {/* TODO: Add captions track and disable warning */}
            {/* eslint-disable-next-line */}
            <video id={'full-code'} className="w-10/12" controls>
              <source src={OptionVideos['fullCode']} type="video/mp4" />
            </video>
          </div>
          {/* </div> */}
          <div id="nav" className="mt-4 flex flex-row">
            {patientInfo.navType.base === 'self' && nextPageURL && <NavForward to={nextPageURL} disabled={false} />}
            {patientInfo?.conciergeNavigator?.statementUnlocked && (
              <div className="place-self-center">{nextPageURL && <NextSection to={nextPageURL} />}</div>
            )}
          </div>
        </div>
      </>
    );
  }

  // CONCIERGE-NAVIGATED PATIENTS SEE SPECIFIC CODE VIDEO, ONCE ASSIGNED
  if (patientInfo.navType.base === 'concierge') {
    //
    return (
      <div className="">
        <div className="flex flex-col items-center">
          <p className="w-11/12 text-xl">
            Please wait for your concierge to assign a code status or select the code discussed with your medical
            provider below.
          </p>
        </div>
        <treatmentOptionForm.Field
          name="code"
          children={(field) => (
            <>
              <FormSelect
                label="Code"
                name="code"
                id="code-select"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={async (e) => {
                  updateScript(e.target.value as TreatmentOptionsType);
                  field.handleChange(e.target.value as TreatmentOptionsType);
                }}
                required={true}
                options={treatmentOptionsArray}
              />
              <FieldInfo fieldMeta={field.state.meta} />
            </>
          )}
        />
        <treatmentOptionForm.Field
          name="unAttestedScript"
          validators={{
            onChange({ value }) {
              if (!value) {
                return 'Script cannot be empty';
              } else {
                return undefined;
              }
            },
          }}
          children={(field) => (
            <>
              <ScriptInput
                label="Script Preview"
                id="script-input"
                type="textarea"
                name={field.name}
                value={field.state.value}
                handleBlur={field.handleBlur}
                handleChange={(e) => field.handleChange(e.target.value)}
                required={true}
              />
              <FieldInfo fieldMeta={field.state.meta} />
            </>
          )}
        />
        <div id="treatment-option-page-video" className="flex flex-col items-center justify-center">
          {chart.SELECTED_TREATMENT_OPTION?.code && (
            /* TODO: Add captions track and disable warning */
            /* eslint-disable-next-line */
            <video
              src={OptionVideos[chart.SELECTED_TREATMENT_OPTION.code]}
              id={chart.SELECTED_TREATMENT_OPTION.code}
              className="w-full"
              controls
            />
          )}
        </div>
        <div className="mt-8 flex flex-col items-center">
          <treatmentOptionForm.Subscribe
            selector={(state) => [state.canSubmit, state.errors]}
            children={([canSubmit]) => (
              <>
                {!canSubmit && (
                  <div className="mt-4 flex flex-col items-center">
                    <p className="text-red-500">Please correct any errors above before re-submitting.</p>
                  </div>
                )}
                <Button
                  id="submit-hca-info"
                  type="submit"
                  disabled={!canSubmit}
                  onClick={treatmentOptionForm.handleSubmit}
                  textContent="Submit"
                />
              </>
            )}
          />
        </div>
        {chart.SELECTED_TREATMENT_OPTION?.unAttestedScript && (
          <div className="flex flex-col items-center">{nextPageURL && <NextSection to={nextPageURL} />}</div>
        )}
      </div>
    );
  }

  return <div>Loading...</div>;
}
